import { useEffect, useState } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Muestra un badge en el Dashboard cuando la clienta acaba de enviar el
 * formulario de progreso y su nuevo plan está en la ventana de 24h.
 *
 * Se activa cuando el endpoint plan-ready-status devuelve
 * `update_pending_release=true`.
 */
export default function FitEvolutionPendingReleaseBadge({ user }) {
  const [visible, setVisible] = useState(false);
  const [hoursLeft, setHoursLeft] = useState(null);

  useEffect(() => {
    if (!user?.email) return;
    if (user?.subscription_plan !== 'basic') return;

    let alive = true;
    const load = async () => {
      try {
        const res = await axios.get(
          `${API}/api/personalized-plans/fit-evolution/plan-ready-status/${encodeURIComponent(user.email)}`
        );
        if (!alive) return;
        const pending = !!res.data?.update_pending_release;
        // Si ya pasaron las 24h (next_release_at en el pasado), ocultar el
        // banner aunque el flag siga en la DB — evita el "banner zombie"
        // después de que el plan renovado ya fue entregado.
        let nextReleaseMs = null;
        if (res.data?.next_release_at) {
          nextReleaseMs = new Date(res.data.next_release_at).getTime() - Date.now();
        }
        // 2026-09 (L2 fix, ticket 244425): sin `next_release_at` el banner se
        // quedaba fijo para siempre, porque un timestamp ausente contaba como
        // "todavia en el futuro". Sin fecha no hay ventana de 24h que mostrar.
        setVisible(pending);
        if (pending && nextReleaseMs !== null) {
          setHoursLeft(nextReleaseMs > 0 ? Math.ceil(nextReleaseMs / (60 * 60 * 1000)) : 0);
        }
      } catch {
        // silent
      }
    };
    load();
    return () => { alive = false; };
  }, [user]);

  if (!visible) return null;

  return (
    <div
      className="rounded-2xl p-4 md:p-5 mb-4 flex items-start gap-3"
      style={{
        background: 'linear-gradient(135deg, rgba(52,211,153,0.14), rgba(52,211,153,0.05))',
        border: '1.5px solid rgba(52,211,153,0.45)',
      }}
      data-testid="fit-evo-pending-release-badge"
    >
      <div className="text-2xl md:text-3xl leading-none pt-0.5">⏳</div>
      <div className="flex-1 min-w-0">
        <p className="text-[10px] uppercase tracking-widest text-emerald-300 font-bold mb-1">
          Ajuste en camino
        </p>
        <h3 className="text-base md:text-lg font-black text-white mb-0.5" style={{ fontFamily: 'Poppins, sans-serif' }}>
          Tu coach liberará tu plan renovado
        </h3>
        <p className="text-xs md:text-sm text-white/80 leading-snug">
          Mientras tanto seguí con tu plan actual sin cambios.
          {hoursLeft !== null && hoursLeft > 0 && (
            <> Faltan aproximadamente <strong className="text-emerald-200">{hoursLeft}h</strong>.</>
          )}
        </p>
      </div>
    </div>
  );
}
